/**
 * Google Sign-In for RESQLY on Android and the web.
 *
 * Android uses Capacitor Social Login. Browsers use Google's Identity
 * Services button and send the resulting ID token to the same backend
 * verifier at /auth/google.
 */
import { isNativePlatform } from './providerPush';

let initialized = false;
let googleIdentityScriptPromise = null;

// This public Web OAuth client ID is already configured for the native flow
// in frontend/android/app/src/main/res/values/strings.xml. Keep a source-level
// fallback so the website still renders its Google button if a host omits the
// frontend build variable; resqly.in must also be allowed in Google Cloud Console.
const DEFAULT_GOOGLE_WEB_CLIENT_ID = '948902418381-i6l2p8a1os3chrohi4c411mb8f8k5ld8.apps.googleusercontent.com';

/**
 * Reject empty or documentation-placeholder OAuth client IDs. Client IDs are
 * public configuration values, but they must correspond to a real Web client
 * created in the RESQLY Google Cloud project.
 */
function isUsableGoogleClientId(value) {
  const id = String(value || '').trim();
  if (!id) return false;

  const normalized = id.toLowerCase();
  return ![
    'your_google',
    'your-google',
    'your_web_client_id',
    'your-web-client-id',
    'placeholder',
    'replace-me',
  ].some((marker) => normalized.includes(marker));
}

/**
 * Load Google Identity Services once for browser-based authentication.
 */
function loadGoogleIdentityServices() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(new Error('Google sign-in is only available in a browser'));
  }

  if (window.google?.accounts?.id) {
    return Promise.resolve(window.google);
  }

  if (googleIdentityScriptPromise) {
    return googleIdentityScriptPromise;
  }

  googleIdentityScriptPromise = new Promise((resolve, reject) => {
    const scriptUrl = 'https://accounts.google.com/gsi/client';
    let script = document.querySelector(`script[src="${scriptUrl}"]`);

    const handleLoad = () => {
      if (window.google?.accounts?.id) {
        resolve(window.google);
      } else {
        googleIdentityScriptPromise = null;
        reject(new Error('Google sign-in could not initialize. Please refresh and try again.'));
      }
    };

    const handleError = () => {
      googleIdentityScriptPromise = null;
      reject(new Error('Google sign-in could not load. Check your connection and try again.'));
    };

    if (!script) {
      script = document.createElement('script');
      script.src = scriptUrl;
      script.async = true;
      script.defer = true;
    }

    script.addEventListener('load', handleLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });

    if (!script.isConnected) {
      document.head.appendChild(script);
    }
  });

  return googleIdentityScriptPromise;
}

/**
 * Use Google as the primary sign-in method on both web and native platforms.
 * Web-specific client ID validation happens while creating the web button.
 */
export function useGooglePrimaryAuth() {
  return true;
}

export function getGoogleWebClientId() {
  const configured = process.env.REACT_APP_GOOGLE_WEB_CLIENT_ID;
  return isUsableGoogleClientId(configured)
    ? configured.trim()
    : DEFAULT_GOOGLE_WEB_CLIENT_ID;
}

export function getGoogleAndroidClientId() {
  return process.env.REACT_APP_GOOGLE_ANDROID_CLIENT_ID || '';
}

export function validateGoogleAuthConfig() {
  if (!isNativePlatform()) {
    return { ok: true };
  }
  const webClientId = getGoogleWebClientId();
  if (!isUsableGoogleClientId(webClientId)) {
    return {
      ok: false,
      error:
        'Google Sign-In is not configured. Set REACT_APP_GOOGLE_WEB_CLIENT_ID for Android builds.',
    };
  }
  return { ok: true };
}

/**
 * Mount the official Google sign-in button for browsers.
 * The ID token is handled by the caller and verified by the RESQLY backend.
 */
export async function initializeGoogleWebButton(element, onCredential) {
  if (!element) {
    throw new Error('Google sign-in button container is unavailable');
  }

  const webClientId = getGoogleWebClientId().trim();
  if (!isUsableGoogleClientId(webClientId)) {
    throw new Error(
      'Google sign-in is not configured for the website. Set REACT_APP_GOOGLE_WEB_CLIENT_ID in the production environment.'
    );
  }

  const google = await loadGoogleIdentityServices();
  google.accounts.id.initialize({
    client_id: webClientId,
    callback: (response) => {
      if (response?.credential) {
        onCredential(response.credential);
      }
    },
    auto_select: false,
    cancel_on_tap_outside: true,
  });

  google.accounts.id.renderButton(element, {
    type: 'standard',
    theme: 'outline',
    size: 'large',
    text: 'continue_with',
    shape: 'rectangular',
    logo_alignment: 'left',
    width: Math.min(element.clientWidth || 340, 400),
    locale: 'en',
  });
}

export function classifyGoogleAuthError(error) {
  if (!error) {
    return 'Google sign-in failed';
  }
  if (isGoogleSignInCancelled(error)) {
    return null;
  }
  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return 'Request timed out. Check your connection and try again.';
  }
  if (!error.response) {
    return error.message?.includes('Google sign-in')
      ? error.message
      : 'Cannot reach RESQLY servers. Check your internet connection and try again.';
  }
  const status = error.response.status;
  if (status === 401) {
    return error.response.data?.detail || 'Google sign-in expired or invalid. Please try again.';
  }
  if (status === 409) {
    return error.response.data?.detail || 'This account conflicts with an existing RESQLY profile.';
  }
  if (status >= 500) {
    return 'RESQLY is temporarily unavailable. Please try again in a moment.';
  }
  return error.response?.data?.detail || error.message || 'Google sign-in failed';
}

export async function initializeGoogleAuth() {
  if (!isNativePlatform()) return;
  const configCheck = validateGoogleAuthConfig();
  if (!configCheck.ok) {
    throw new Error(configCheck.error);
  }
  if (initialized) return;
  const { SocialLogin } = await import('@capgo/capacitor-social-login');
  // webClientId is used for ALL platforms (migration guide). mode:'online'
  // makes login() resolve with an idToken (offline mode only returns a
  // serverAuthCode, which our /auth/google backend does not accept).
  await SocialLogin.initialize({
    google: {
      webClientId: getGoogleWebClientId(),
      mode: 'online',
    },
  });
  initialized = true;
}

export async function signInWithGoogle() {
  if (!isNativePlatform()) {
    throw new Error('Use the Google Identity Services button for browser sign-in');
  }
  await initializeGoogleAuth();
  const { SocialLogin } = await import('@capgo/capacitor-social-login');
  const res = await SocialLogin.login({
    provider: 'google',
    options: {
      scopes: ['email', 'profile'],
    },
  });
  const idToken = res?.result?.idToken;
  if (!idToken) {
    throw new Error('Google sign-in did not return a credential');
  }
  return idToken;
}

export function isGoogleSignInCancelled(error) {
  const message = String(error?.message || error || '').toLowerCase();
  const code = String(error?.code || '').toLowerCase();
  return (
    message.includes('cancel') ||
    message.includes('closed') ||
    message.includes('user cancelled') ||
    message.includes('12501') ||
    code.includes('sign_in_canceled') ||
    code.includes('12501')
  );
}
